import { useState, useEffect, useCallback, useRef } from "react";
import { ScrollFade } from "@/components/ScrollFade";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PostPanel, RetractButton } from "@/components/PostPanel";
import { LinkedInPreview } from "@/components/LinkedInPreview";
import { Mic, RefreshCw, KeyRound, FlaskConical, ArrowLeft, ExternalLink, Eye, X, Upload, Linkedin } from "lucide-react";

const MANQUE = { nom: "nom", theme: "thème", accroche: "accroche", accord_linkedin: "accord LinkedIn" };
const ETATS = {
  incomplet: ["Incomplet", "bg-slate-100 text-slate-600"],
  attente_accord: ["En attente de l'accord du conférencier", "bg-amber-50 text-amber-700"],
  refuse: ["Refusé par le conférencier", "bg-red-50 text-red-700"],
  pret: ["Prêt", "bg-emerald-50 text-emerald-700"],
  programme: ["Programmé", "bg-sky-50 text-sky-700"],
  attente_clic: ["En attente d'un clic « Publier »", "bg-violet-50 text-violet-700"],
  a_la_main: ["À publier à la main", "bg-orange-50 text-orange-700"],
  texte_a_revoir: ["Texte à revoir : ville modifiée", "bg-amber-100 text-amber-800"],
  publie: ["Publié", "bg-emerald-100 text-emerald-800"],
  republie: ["Republié par APOLLO France", "bg-emerald-100 text-emerald-800"],
  retire: ["Retiré", "bg-slate-200 text-slate-600"],
  post_retire: ["Post retiré de LinkedIn", "bg-slate-200 text-slate-600"],
  passee: ["Passée", "bg-slate-100 text-slate-400"],
  page_a_connecter: ["Page du groupe à connecter", "bg-amber-100 text-amber-800"],
  erreur: ["Erreur", "bg-red-100 text-red-800"],
};

const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }) : "");
const fmtHeure = (h) => (h || "07:00").replace(/^0(\d)/, "$1").replace(":", "h").replace(/h00$/, "h");
const pl = (n, w) => `${n} ${w}${n > 1 ? "s" : ""}`;
const fmtDay = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("fr-FR") : "");
const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

function useNoindex() {
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    const prev = meta?.getAttribute("content");
    meta?.setAttribute("content", "noindex, nofollow");
    const title = document.title;
    document.title = "Conférences APOLLO | Pulse";
    return () => { if (meta && prev) meta.setAttribute("content", prev); document.title = title; };
  }, []);
}

const Statut = ({ c, onChanged }) => {
  const [label0, cls] = ETATS[c.etat] || ETATS.erreur;
  const label = c.etat === "texte_a_revoir" && c.texte_a_revoir?.champ === "nom" ? "Texte à revoir : nom modifié" : label0;
  const extra = c.etat === "incomplet" || c.etat === "attente_accord"
    ? c.manque.filter((m) => m !== "accord_linkedin").map((m) => MANQUE[m] || m) : [];
  return (
    <div>
      <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}{c.etat === "programme" && c.programme_a ? ` à ${fmtTs(c.programme_a)}` : ""}</span>
      {extra.length > 0 && <p className="text-xs text-slate-500 mt-1">Manque : {extra.join(", ")}</p>}
      {c.post_url && <a href={c.post_url} target="_blank" rel="noreferrer" className="text-xs text-coral-dark inline-flex items-center gap-1 mt-1">Post <ExternalLink className="h-3 w-3" /></a>}
      {c.post_obsolete && <p className="text-xs text-amber-700 mt-1" data-testid={`post-obsolete-${c.id}`}>Conférence modifiée depuis la publication</p>}
      {c.repost_url && <a href={c.repost_url} target="_blank" rel="noreferrer" className="text-xs text-coral-dark inline-flex items-center gap-1 mt-1 ml-2">Republication <ExternalLink className="h-3 w-3" /></a>}
      {c.etat === "erreur" && c.erreur_message && <p className="text-xs text-red-700 mt-1" data-testid={`conference-error-${c.id}`}>{c.erreur_message}</p>}
      {c.post_urn && !c.retrait_le && (c.etat === "retire" || c.statut_api !== "pret" || c.post_obsolete) && <RetractButton c={c} onDone={onChanged} />}
    </div>
  );
};

function Preview({ conf, essai, onClose, onRefresh }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const timer = useRef(null);
  const alive = useRef(true);
  const reqId = useRef(0);
  const [post, setPost] = useState(null);
  const [postErr, setPostErr] = useState("");
  const [tab, setTab] = useState("visuel");
  const ready = conf.statut_api === "pret" || ["pret", "a_la_main", "texte_a_revoir"].includes(conf.etat);
  useEffect(() => {
    if (!ready) return;
    let on = true;
    api.get(`/admin/conferences/${encodeURIComponent(conf.id)}/post`, { params: { essai } })
      .then((r) => on && setPost(r.data))
      .catch((e) => on && setPostErr(formatApiError(e.response?.data?.detail)));
    return () => { on = false; };
  }, [conf.id, essai, ready]);
  const load = useCallback(async () => {
    const id = ++reqId.current;
    try {
      const r = await api.get(`/admin/conferences/${encodeURIComponent(conf.id)}/visual`, { params: { essai } });
      if (!alive.current || id !== reqId.current) return;
      if (r.status === 202) { setRes({ wait: true }); timer.current = setTimeout(load, 8000); return; }
      setErr("");
      setRes(r.data);
    } catch (e) { if (alive.current && id === reqId.current) { setRes(null); setErr(formatApiError(e.response?.data?.detail)); } }
  }, [conf.id, essai]);
  useEffect(() => {
    alive.current = true;
    load();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => { alive.current = false; clearTimeout(timer.current); window.removeEventListener("keydown", onKey); };
  }, [load, onClose]);
  const validate = async () => {
    try { await api.post(`/admin/conferences/generic/${res.image_generique}/valider`); toast.success("Image validée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const regenerate = async () => {
    try { await api.post(`/admin/conferences/generic/${res.image_generique}/regenerer`); setRes(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const replace = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/conferences/generic/${res.image_generique}`, fd); setRes(null); load(); toast.success("Image générique remplacée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="visual-preview-modal" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm max-w-6xl w-full max-h-[92vh] overflow-y-auto p-6 grid md:grid-cols-[minmax(0,1fr)_420px] gap-6">
        <div>
          {err && <p className="text-red-700 text-sm" data-testid="visual-preview-error">{err}</p>}
          {!res && !err && <p className="text-sm text-slate-500">Rendu en cours…</p>}
          {res?.wait && <p className="text-sm text-slate-500" data-testid="visual-preview-waiting">Image générique en préparation pour cette activité (environ une minute)…</p>}
          {res?.image && ready && (
            <div className="flex gap-1 mb-3 text-xs font-semibold" data-testid="preview-tabs">
              {[["visuel", "Visuel"], ["linkedin", "Aperçu LinkedIn"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`preview-tab-${k}`} className={`px-3 py-1.5 rounded-sm border ${tab === k ? "bg-ink text-white border-ink" : "border-slate-300 text-slate-600 hover:border-coral"}`}>{l}</button>
              ))}
            </div>
          )}
          {res?.image && tab === "linkedin" && ready && (post ? <LinkedInPreview texte={post.texte} image={res.image} pageNom={post.page_nom} pageLogo={post.page_logo} connectee={post.page_connectee} /> : <p className="text-sm text-slate-500">Rédaction du post…</p>)}
          {res?.image && (tab === "visuel" || !ready) && <img src={res.image} alt="Visuel de la conférence" className="w-full border border-slate-200" data-testid="visual-preview-image" />}
        </div>
        <div className="text-sm space-y-4">
          <button onClick={onClose} data-testid="visual-preview-close" className="ml-auto flex items-center gap-1 text-slate-500 hover:text-ink"><X className="h-4 w-4" /> Fermer</button>
          {res?.image && <>
            <div><p className="text-xs uppercase tracking-[0.12em] text-slate-400 mb-1">Thème d'origine (post)</p><p data-testid="visual-theme-original">{res.theme_original}</p></div>
            <div><p className="text-xs uppercase tracking-[0.12em] text-slate-400 mb-1">Thème du visuel</p><p data-testid="visual-theme-visuel">{res.theme_visuel}</p>
              <p className="text-xs text-slate-500 mt-1">{res.raccourci ? "Version courte, pour le visuel uniquement." : "Identique au thème d'origine."}</p></div>
            <p className="text-xs text-slate-500">Rendu en {res.rendu_ms} ms · ville : {res.ville || "aucune"}</p>
            {res.photo_note && <p className="rounded-sm px-3 py-2 text-xs bg-slate-100 text-slate-700" data-testid="visual-photo-note">{res.photo_note}</p>}
            {res.image_generique && (
              <div className={`rounded-sm px-3 py-2 text-xs ${res.image_generique_validee ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="visual-generic-status">
                {res.image_generique_validee ? "Image générique validée." : "Image générique pas encore validée : aucune publication possible tant qu'elle ne l'est pas."}
              </div>
            )}
            {res.image_generique && !res.image_generique_validee && (
              <button onClick={validate} data-testid="visual-generic-validate" className="w-full bg-ink text-white text-xs font-semibold px-3 py-2 rounded-sm hover:bg-slate-800">Valider cette image</button>
            )}
            {res.image_generique && (
              <button onClick={regenerate} data-testid="visual-generic-regenerate" className="w-full border border-slate-300 text-xs font-semibold px-3 py-2 rounded-sm hover:border-coral">Régénérer</button>
            )}
            {res.image_generique && (
              <label className="inline-flex items-center gap-2 text-xs font-semibold border border-slate-300 px-3 py-2 rounded-sm cursor-pointer hover:border-coral" data-testid="visual-generic-replace">
                <Upload className="h-3.5 w-3.5" /> Remplacer l'image générique ({res.image_generique})
                <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => e.target.files[0] && replace(e.target.files[0])} />
              </label>
            )}
          </>}
          {res?.image && ready && <PostPanel conf={conf} essai={essai} visual={res} post={post} err={postErr} setPost={setPost} onChanged={() => { load(); onRefresh?.(); }} />}
        </div>
      </div>
    </div>
  );
}

const ConfTable = ({ rows, onPreview, onChanged }) => (
  <ScrollFade className="bg-white border border-slate-200 rounded-sm">
    <table className="w-full text-sm" data-testid="conferences-table">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500">
        <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Groupe</th><th className="px-4 py-3">Conférencier</th><th className="px-4 py-3">Thème</th><th className="px-4 py-3">Statut</th></tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Aucune conférence dans la fenêtre.</td></tr>}
        {rows.map((c) => (
          <tr key={c.id} className="border-t border-slate-100 align-top" data-testid={`conference-row-${c.id}`}>
            <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.reunion?.date)}<span className="text-slate-400"> · {fmtHeure(c.reunion?.heure)}</span></td>
            <td className="px-4 py-3"><span data-testid={`conference-group-${c.id}`}>{c.nom_affiche || c.groupe?.nom}</span>{c.nom_affiche && c.nom_affiche !== c.groupe?.nom && <p className="text-[11px] text-slate-400">Nom API : {c.groupe?.nom}</p>}<p className="text-xs text-slate-400">{c.ville_posts || c.groupe?.ville}</p></td>
            <td className="px-4 py-3">{c.conferencier ? <><span className="font-semibold">{c.conferencier.prenom} {c.conferencier.nom}</span><p className="text-xs text-slate-500">{[c.conferencier.activite, c.conferencier.societe].filter(Boolean).join(", ")}</p></> : <span className="text-slate-300">Non communiqué</span>}</td>
            <td className="px-4 py-3 max-w-xs">{c.theme || <span className="text-slate-300">Non communiqué</span>}</td>
            <td className="px-4 py-3"><Statut c={c} onChanged={onChanged} />
              {c.conferencier && c.theme && c.etat !== "passee" && <button onClick={() => onPreview(c)} data-testid={`conference-preview-${c.id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-coral-dark hover:underline"><Eye className="h-3.5 w-3.5" /> Aperçu du visuel</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </ScrollFade>
);

const CallLog = ({ items }) => (
  <ScrollFade className="bg-white border border-slate-200 rounded-sm">
    <table className="w-full text-xs" data-testid="conferences-api-log">
      <thead className="bg-slate-50 text-left uppercase tracking-[0.12em] text-slate-500">
        <tr><th className="px-3 py-2">Heure</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Code</th><th className="px-3 py-2">Groupes</th><th className="px-3 py-2">Conférences</th><th className="px-3 py-2">Erreur</th></tr>
      </thead>
      <tbody>
        {items.map((a, i) => (
          <tr key={i} className="border-t border-slate-100">
            <td className="px-3 py-2">{fmtTs(a.at)}</td>
            <td className="px-3 py-2">{a.source}{a.essai ? " (essai)" : ""}</td>
            <td className={`px-3 py-2 font-semibold ${a.ok ? "text-emerald-700" : "text-red-700"}`}>{a.code ?? "aucun"}</td>
            <td className="px-3 py-2">{a.groupes ?? ""}</td>
            <td className="px-3 py-2">{a.conferences ?? ""}</td>
            <td className="px-3 py-2">{a.erreur || ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </ScrollFade>
);

export default function AdminConferences() {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return <ConferencesPanel />;
}

function ConferencesPanel() {
  useNoindex();
  const [essai, setEssai] = useState(false);
  const [data, setData] = useState(null);
  const [log, setLog] = useState({ appels: [], alertes: [] });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const closePreview = useCallback(() => setPreview(null), []);
  const [showPast, setShowPast] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: l }] = await Promise.all([
        api.get("/admin/conferences", { params: { essai } }), api.get("/admin/conferences/log"),
      ]);
      setData(d); setLog(l);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [essai]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/conferences/refresh", null, { params: { essai } });
      r.ok ? toast.success(`Réponse ${r.code} : ${pl(r.groupes, "groupe")}, ${pl(r.conferences, "conférence")}`) : r.erreur === "lecture_en_cours" ? toast.info("Une lecture de l'API est déjà en cours sur le serveur : réessayez dans une minute.") : toast.error(`Échec de l'appel (${r.code ?? "aucune réponse"} ${r.erreur || ""})`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-conferences-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-coral-dark mb-6"><ArrowLeft className="h-4 w-4" /> Administration</Link>
      <div className="mb-2 flex items-center gap-2 text-coral-dark"><Mic className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.2em]">Section privée</span></div>
      <h1 className="font-serif text-4xl text-slate-900 mb-6">Conférences APOLLO</h1>

      {data && !data.cle_ok && (
        <div data-testid="apollo-key-missing" className="flex items-center gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-5 py-4 mb-6">
          <KeyRound className="h-5 w-5" /><span className="font-semibold">Clé APOLLO à saisir</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button data-testid="conferences-refresh-btn" onClick={refresh} disabled={busy || (data && !data.cle_ok)}
          className="inline-flex items-center gap-2 bg-ink text-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 transition-colors">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Actualiser
        </button>
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
          <input data-testid="conferences-essai-toggle" type="checkbox" checked={essai} onChange={(e) => setEssai(e.target.checked)} className="h-4 w-4 accent-orange-500" />
          <FlaskConical className="h-4 w-4" /> Données d'essai
        </label>
        {!essai && <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input data-testid="conferences-show-past-toggle" type="checkbox" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} className="h-4 w-4 accent-orange-500" />
          Afficher les conférences passées{data ? ` (${data.conferences.filter((c) => c.etat === "passee").length})` : ""}
        </label>}
        <Link to="/admin/conferences/groupes" data-testid="group-pages-link" className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 rounded-sm text-sm font-semibold text-slate-700 hover:border-coral"><Linkedin className="h-4 w-4" /> Pages LinkedIn des groupes</Link>
        {data?.lu_le && <span className="text-xs text-slate-500">{essai ? "Lecture des données d'essai" : "Dernière lecture réussie"} : {fmtTs(data.lu_le)}{data.fenetre ? ` · fenêtre du ${fmtDay(data.fenetre.du)} au ${fmtDay(data.fenetre.au)}` : ""}</span>}
      </div>

      {essai && (
        <div data-testid="conferences-essai-banner" className="bg-[#F77F00] text-[#141518] font-extrabold tracking-[0.3em] text-center py-2 rounded-sm mb-4">ESSAI · DONNÉES FICTIVES, JAMAIS PUBLIÉES</div>
      )}

      {log.alertes.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-sm px-5 py-3 mb-6 text-sm text-red-800" data-testid="conferences-alerts">
          {log.alertes.slice(0, 3).map((a, i) => <p key={i}>{fmtTs(a.at)} : {a.message}</p>)}
        </div>
      )}

      {data && <ConfTable rows={data.conferences.filter((c) => showPast || c.etat !== "passee")} onPreview={setPreview} onChanged={load} />}
      {preview && <Preview conf={preview} essai={essai} onClose={closePreview} onRefresh={load} />}
      {data && !essai && <p className="text-xs text-slate-400 mt-2">{data.groupes.length} groupes connus.</p>}

      <h2 className="font-serif text-xl text-ink mt-10 mb-3">Journal des appels à l'API</h2>
      <CallLog items={log.appels} />
    </div>
  );
}
